import { useMemo, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import PropTypes from 'prop-types';

import { FieldWrap, HelperText, LabelStatic, Wrap } from '../_shared/styled';
import { AmountSymbols, FieldInput, FieldLabel, HelperBlock } from './styled';
import { useResizableTextArea } from './useResizableTextArea';

TextareaField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  name: PropTypes.string,
  helperText: PropTypes.string,
  height: PropTypes.number,
  maxSymbols: PropTypes.number,
  variant: PropTypes.string,
  labelStatic: PropTypes.bool,
  fullWidth: PropTypes.bool,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  resizableWithText: PropTypes.bool,
  multiline: PropTypes.bool,
  onChange: PropTypes.func,
};

function TextareaField({
  name,
  label,
  value,
  helperText,
  height,
  placeholder,
  maxSymbols,
  variant,
  resizableWithText,
  labelStatic = true,
  disabled = false,
  fullWidth = false,
  error = false,
  multiline,
  isBlackFont = false,
  onChange = () => {},
}) {
  const textareaRef = useRef(null);
  const [isFocus, setIsFocus] = useState(false);

  const onFocus = () => {
    setIsFocus(true);
  };

  const onBlur = () => {
    setIsFocus(false);
  };

  const isFilled = useMemo(() => {
    return value.length > 0;
  }, [value]);

  useResizableTextArea({ textareaRef, resizableWithText, value, height });

  return (
    <Wrap fullWidth={fullWidth}>
      {labelStatic && (
        <LabelStatic
          htmlFor={name}
          error={error}
          disabled={disabled}
        >
          {label}
        </LabelStatic>
      )}
      <FieldWrap>
        {!labelStatic && (
          <FieldLabel
            htmlFor={name}
            focus={isFocus}
            activated={isFilled}
            disabled={disabled}
            error={error}
          >
            {label}
          </FieldLabel>
        )}
        <FieldInput
          ref={textareaRef}
          id={name}
          height={height}
          error={error}
          value={value}
          activated={isFilled}
          labelStatic={labelStatic}
          placeholder={placeholder}
          variant={variant}
          focus={isFocus}
          isBlackFont={isBlackFont}
          onChange={onChange}
          disabled={disabled}
          onFocus={onFocus}
          onBlur={onBlur}
          multiline={multiline}
          resizableWithText={resizableWithText}
        />
      </FieldWrap>
      <HelperBlock>
        <HelperText>{error && helperText}</HelperText>
        {maxSymbols && (
          <AmountSymbols>
            {value.length} / {maxSymbols}
          </AmountSymbols>
        )}
      </HelperBlock>
    </Wrap>
  );
}

RTextareaField.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  control: PropTypes.any,
};

function RTextareaField({ label, name, control, isBlackFont, ...rest }) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <TextareaField
          name={name}
          label={label}
          value={value || ''}
          onChange={onChange}
          error={!!error}
          helperText={error?.message ?? ''}
          isBlackFont={isBlackFont}
          {...rest}
        />
      )}
    />
  );
}

export { RTextareaField };
